// Full-spec launch form + pinned receipt panel (simulations.md). The spec
// fields themselves live in `BatchSpecFields` — shared verbatim with Template
// authoring, so the two surfaces cannot drift (docs/14 §5: a Template is a
// saved launch spec and there is no second launch path). What stays here is
// what only a LAUNCH has: the batch name, the template provenance select, and
// the checkout-style receipt — the high-level selections plus the live
// debounced `:preview` result, where "this will launch N runs" is the seatbelt
// (docs/14 §2) and Launch stays disabled until the preview is clean.
// Props-pure; the container owns queries, the preview mutation, and launch.
import type { components } from "../../api/schema";
import { docUrl } from "../../lib/docLinks";
import {
  ResolvedCellView,
  resolvedCellSuppliers,
} from "../variables/ResolvedCellView";
import {
  Badge,
  Button,
  HelpTip,
  Inline,
  InlineError,
  KeyValue,
  PageHeader,
  Section,
  Select,
  Stack,
  Text,
  TextInput,
} from "../../ui";
import { BatchSpecFields, labelFor, optionItems } from "./BatchSpecFields";
import {
  launchDiagnosis,
  orderValidationGroups,
  validationExampleMessage,
  warningGroupMessage,
} from "./launchDiagnosis";
import {
  isPreviewable,
  missingLaunchRequirements,
  parseTokenList,
  parseVariableSweeps,
  type SimulationLaunchState,
  type VariableDeclaration,
  variableSweepErrors,
} from "./simulationLaunchState";
import type { LaunchOption } from "./useLaunchOptions";

type BatchPreviewResponse = components["schemas"]["BatchPreviewResponse"];

export interface SimulationLaunchFormProps {
  agentId: string;
  connectionOptions: LaunchOption[];
  errorMessage?: string;
  isLaunching: boolean;
  isPreviewPending: boolean;
  isSavingTemplate: boolean;
  isTemplateSaveable: boolean;
  onChange: (state: SimulationLaunchState) => void;
  onLaunch: () => void;
  onRetryOptions?: () => void;
  onRetryPreview?: () => void;
  onSaveAsTemplate: () => void;
  onTemplateSelect: (templateId: string) => void;
  personaOptions: LaunchOption[];
  preview?: BatchPreviewResponse;
  previewErrorMessage?: string;
  scenarioOptions: LaunchOption[];
  scenarioSetOptions: LaunchOption[];
  state: SimulationLaunchState;
  templateOptions: LaunchOption[];
  variables: VariableDeclaration[];
  versionOptions: LaunchOption[];
}

export function SimulationLaunchForm({
  agentId,
  connectionOptions,
  errorMessage,
  isLaunching,
  isPreviewPending,
  isSavingTemplate,
  isTemplateSaveable,
  onChange,
  onLaunch,
  onRetryOptions,
  onRetryPreview,
  onSaveAsTemplate,
  onTemplateSelect,
  personaOptions,
  preview,
  previewErrorMessage,
  scenarioOptions,
  scenarioSetOptions,
  state,
  templateOptions,
  variables,
  versionOptions,
}: SimulationLaunchFormProps) {
  const patch = (partial: Partial<SimulationLaunchState>) => {
    onChange({ ...state, ...partial });
  };

  const validationGroups = orderValidationGroups(
    preview?.validation_groups ?? [],
  );
  const warningGroups = preview?.warning_groups ?? [];
  const resolvedCells = (preview?.cells ?? []).filter(
    (cell) => Object.keys(cell.variables ?? {}).length > 0,
  );
  const isPreviewClean =
    preview !== undefined &&
    validationGroups.length === 0 &&
    previewErrorMessage === undefined;
  const canLaunch =
    isPreviewable(state, variables) &&
    state.displayName.trim().length > 0 &&
    isPreviewClean &&
    !isPreviewPending &&
    !isLaunching;
  const missingRequirements = missingLaunchRequirements(state);

  const typedSweepErrors = variableSweepErrors(state.sweepVariables, variables);
  const sweepCellCount =
    Math.max(1, parseTokenList(state.sweepLanguage).length || 1) *
    Math.max(1, parseTokenList(state.sweepAccent).length || 1) *
    Math.max(1, parseTokenList(state.sweepBackground).length || 1) *
    Math.max(
      1,
      parseVariableSweeps(state.sweepVariables, variables).length || 1,
    );

  // Route-level Authoring (UI-LAW §4c): Launch (primary) and Save as template
  // ride a fixed PageHeader above the independently scrolling receipt.
  return (
    <div className="marker-simulation-create marker-simulation-create--launch">
      <Stack className="marker-simulation-create__form" gap={6}>
        <Section heading="Batch">
          <Stack gap={3}>
            <TextInput
              label="Batch name"
              required
              value={state.displayName}
              onChange={(event) => {
                patch({ displayName: event.currentTarget.value });
              }}
            />
            <Select
              help={
                <HelpTip
                  docHref={docUrl("concepts/simulated-testing")}
                  docLabel="Simulated testing"
                  label="About Templates"
                >
                  {
                    "A Template is a saved launch specification. Picking one loads its saved launch inputs below."
                  }
                </HelpTip>
              }
              label="Template"
              value={state.templateId}
              onChange={(event) => {
                const templateId = event.currentTarget.value;
                patch({ templateId });
                onTemplateSelect(templateId);
              }}
            >
              <option value="">No template</option>
              {optionItems(templateOptions)}
            </Select>
          </Stack>
        </Section>

        <BatchSpecFields
          advancedDisclosure
          agentId={agentId}
          connectionOptions={connectionOptions}
          onChange={onChange}
          personaOptions={personaOptions}
          scenarioSetOptions={scenarioSetOptions}
          state={state}
          variables={variables}
          versionOptions={versionOptions}
        />
      </Stack>

      <aside className="marker-simulation-create__receipt">
        <PageHeader
          actions={
            <>
              {errorMessage !== undefined ? (
                <InlineError>{errorMessage}</InlineError>
              ) : null}
              {onRetryOptions ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onRetryOptions}
                >
                  Retry options
                </Button>
              ) : null}
              <Button
                disabled={!isTemplateSaveable || isSavingTemplate}
                type="button"
                variant="secondary"
                onClick={onSaveAsTemplate}
              >
                {isSavingTemplate ? "Saving template…" : "Save as template"}
              </Button>
              <Button disabled={!canLaunch} type="button" onClick={onLaunch}>
                {isLaunching ? "Launching…" : "Launch simulation"}
              </Button>
            </>
          }
          className="marker-simulation-create__actions"
          compact
        />
        <div className="marker-simulation-create__receipt-scroll">
          <Stack gap={4}>
            <Text variant="caps">Launch summary</Text>
            <KeyValue
              rows={[
                {
                  label: "Version",
                  value: labelFor(
                    versionOptions,
                    state.agentVersionId,
                    "Not selected",
                  ),
                },
                {
                  label: "Connection",
                  value: labelFor(
                    connectionOptions,
                    state.connectionId,
                    "Not selected",
                  ),
                },
                {
                  label: "Personas",
                  value:
                    state.personaIds.length === 0
                      ? "Not selected"
                      : `${String(state.personaIds.length)} selected`,
                },
                {
                  label: "Scenario set",
                  value: labelFor(
                    scenarioSetOptions,
                    state.scenarioSetId,
                    "Not selected",
                  ),
                },
                {
                  label: "Sweep cells",
                  value: String(sweepCellCount),
                },
                {
                  label: "Sample",
                  value: state.sampleEnabled
                    ? `${state.sampleSize || "?"} of expansion (seed ${state.sampleSeed || "?"})`
                    : "Full expansion",
                },
              ]}
            />
            {isPreviewPending ? (
              <Text variant="muted">Previewing…</Text>
            ) : preview !== undefined ? (
              <Stack gap={2}>
                <Inline align="center" gap={2}>
                  <Text>This will launch</Text>
                  <Badge
                    tone={validationGroups.length === 0 ? "success" : "danger"}
                  >
                    {`${String(preview.estimated_usage.simulated_cells)} runs`}
                  </Badge>
                </Inline>
                <Text variant="muted">
                  {preview.expanded_count} expanded · {preview.eligible_count}{" "}
                  eligible
                  {preview.concurrency.effective_concurrency != null
                    ? ` · effective concurrency ≤ ${String(preview.concurrency.effective_concurrency)}`
                    : ""}
                  {preview.concurrency.batch_concurrency_max !== null
                    ? ` · concurrency ≤ ${String(
                        preview.concurrency.batch_concurrency_max,
                      )}`
                    : ""}
                </Text>
                {preview.clamped ? (
                  <Text variant="muted">
                    {preview.notice ??
                      "Resolved cell details are limited in this preview."}
                  </Text>
                ) : null}
              </Stack>
            ) : missingRequirements.length > 0 ? (
              <Stack
                aria-label="Missing launch requirements"
                aria-live="polite"
                gap={2}
              >
                <Text variant="muted">To launch, select:</Text>
                <Inline gap={2}>
                  {missingRequirements.map((requirement) => (
                    <Badge key={requirement.id} tone="neutral">
                      {requirement.label}
                    </Badge>
                  ))}
                </Inline>
              </Stack>
            ) : (
              <Text variant="muted">
                Complete the sample and variable sweep to preview.
              </Text>
            )}
            {previewErrorMessage !== undefined ? (
              <Inline gap={2}>
                <InlineError>{previewErrorMessage}</InlineError>
                {onRetryPreview ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onRetryPreview}
                  >
                    Retry preview
                  </Button>
                ) : null}
              </Inline>
            ) : null}
            {typedSweepErrors.map((message) => (
              <InlineError key={message}>{message}</InlineError>
            ))}
            {validationGroups.length > 0 ? (
              <Stack
                aria-label="Launch validation diagnosis"
                gap={1}
                role="group"
              >
                <Text variant="muted">
                  {preview?.validation_summary.failing_cell_count} of{" "}
                  {preview?.validation_summary.validated_cell_count} validated
                  cells are blocked:
                </Text>
                <Text variant="muted">
                  A missing required variable or a token that does not resolve
                  to a scalar in every cell blocks the launch before any run
                  starts.
                </Text>
                {validationGroups.map((group) => (
                  <Stack key={`${group.key}:${group.cause}`} gap={1}>
                    {launchDiagnosis(group).map((message, index) =>
                      index === 0 ? (
                        <InlineError key={message}>{message}</InlineError>
                      ) : (
                        <Text key={message} variant="muted">
                          {message}
                        </Text>
                      ),
                    )}
                    {group.examples.length > 0 ? (
                      <Text variant="muted">
                        Examples: {validationExampleMessage(group)}
                      </Text>
                    ) : null}
                    {group.notice !== null ? (
                      <Text variant="muted">{group.notice}</Text>
                    ) : null}
                  </Stack>
                ))}
              </Stack>
            ) : null}
            {warningGroups.length > 0 ? (
              <Stack gap={1}>
                <Text variant="muted">
                  {warningGroups.length} non-blocking warning group
                  {warningGroups.length === 1 ? "" : "s"}:
                </Text>
                {warningGroups.slice(0, 5).map((group) => (
                  <Stack key={group.cause} gap={1}>
                    <Text variant="muted">{warningGroupMessage(group)}</Text>
                    {group.examples.length > 0 ? (
                      <Text variant="muted">
                        Examples: {validationExampleMessage(group)}
                      </Text>
                    ) : null}
                    {group.clamped && group.notice !== null ? (
                      <Text variant="muted">{group.notice}</Text>
                    ) : null}
                  </Stack>
                ))}
                {warningGroups.length > 5 ? (
                  <Text variant="muted">
                    …and {warningGroups.length - 5} more.
                  </Text>
                ) : null}
              </Stack>
            ) : null}
            {resolvedCells.map((cell, index) => {
              return Object.keys(cell.variables ?? {}).length > 0 ? (
                <Stack gap={2} key={cell.cell_key}>
                  <Text variant="caps">
                    Resolved cell {index + 1} of {resolvedCells.length}
                  </Text>
                  <ResolvedCellView
                    supplierLinkTarget="_blank"
                    suppliers={resolvedCellSuppliers(
                      cell,
                      personaOptions,
                      scenarioOptions,
                    )}
                    variables={cell.variables ?? {}}
                  />
                </Stack>
              ) : null;
            })}
          </Stack>
        </div>
      </aside>
    </div>
  );
}
